/*
 * HLTH Free Gift with Purchase
 *
 * Approach: intercept fetch to /cart/add. Parse the request body (handles single-item,
 * multi-item items[], JSON, FormData, URLSearchParams). If any item being added belongs
 * to the trigger product (HLTH Code Complete Meal), append the gift as an additional
 * item in the SAME request. One round-trip, atomic at the server; the caller draws the
 * drawer from the response as it does for any add.
 *
 * Why fetch-level (not form-level) interception: Loop's subscription widget reads the
 * flavor-quantity selector and constructs the multi-variant items[] payload at submit
 * time. If we intercept the form submit before Loop runs, we drop everything except
 * the form's [name="id"] (a single variant). Hooking fetch instead lets Loop build
 * the full payload, then we just augment it.
 *
 * Every add is looked at (16 Oct 2026). The old version waved through any add made while its
 * own cart check was running (just after page load, or just after a basket change), so a Meal
 * Replacement added in that moment went in without the ebook. Now the script's own requests go
 * straight to the server (origFetch), a trigger add that lands while a basket check runs waits
 * for it, and the decision is made from the basket itself:
 *   - the ebook goes in unless the basket already holds it, or the shopper took it out while
 *     keeping the trigger (the opt-out: never re-added to that basket);
 *   - a basket seen without the trigger starts fresh, however it was emptied (/cart/clear.js
 *     used to leave the opt-out note behind);
 *   - backstop: on page load and after any add, a basket holding the trigger but not the ebook
 *     (and no opt-out) gets the ebook in a request of its own;
 *   - the gift never blocks a sale: if the add with the ebook is refused, the shopper's add is
 *     sent again on its own.
 *
 * Opt-out note: keyed to cart.token ("this basket got the ebook") in localStorage, so every tab
 * of the basket shares it. Set whenever the basket holds both, cleared whenever it holds no
 * trigger. The old version's sessionStorage note is still read, then moved.
 *
 * Auto-remove: if the trigger is gone but the gift remains, the gift is stripped via
 * /cart/change and the drawer redrawn without opening it.
 */
(function () {
  const config = window.HLTH_FREE_GIFT;
  if (!config || !config.giftProductId || !config.triggerProductId) return;

  const SOURCE = 'hlth-free-gift';
  const TRIGGER_PRODUCT_ID = Number(config.triggerProductId);
  const GIFT_PRODUCT_ID = Number(config.giftProductId);

  let giftVariantId = Number(config.giftVariantId) || 0;
  const triggerVariantIds = new Set();
  if (Array.isArray(config.triggerVariantIds)) {
    config.triggerVariantIds.forEach((id) => {
      const n = Number(id);
      if (n) triggerVariantIds.add(n);
    });
  }
  let triggerVariantsPromise = null;
  let giftVariantPromise = null;

  // The fetch this script uses for its own requests: they never pass through the wrapper below.
  const origFetch = window.fetch.bind(window);
  const noop = () => {};
  const TIMED_OUT = {};
  function within(promise, ms) {
    return Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(TIMED_OUT), ms))]);
  }

  function ensureTriggerVariants() {
    if (triggerVariantIds.size > 0) return Promise.resolve();
    if (!config.triggerHandle) return Promise.resolve();
    if (!triggerVariantsPromise) {
      triggerVariantsPromise = origFetch(`/products/${config.triggerHandle}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => {
          if (p && p.variants) p.variants.forEach((v) => triggerVariantIds.add(Number(v.id)));
        })
        .catch(noop);
    }
    return triggerVariantsPromise;
  }

  function ensureGiftVariant() {
    if (giftVariantId) return Promise.resolve(giftVariantId);
    if (!config.giftHandle) return Promise.resolve(0);
    if (!giftVariantPromise) {
      giftVariantPromise = origFetch(`/products/${config.giftHandle}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => {
          if (p && p.variants && p.variants.length) giftVariantId = p.variants[0].id;
          return giftVariantId;
        })
        .catch(() => 0);
    }
    return giftVariantPromise;
  }

  // ---- Notes kept in the browser ----
  const GIVEN_KEY = 'hlth_free_gift_added_for_token';
  const REFUSED_KEY = 'hlth_free_gift_refused_for_token';
  function storageGet(area, key) {
    try { return window[area].getItem(key); } catch (e) { return null; }
  }
  function storageSet(area, key, value) {
    try { window[area].setItem(key, value); return true; } catch (e) { return false; }
  }
  function storageRemove(area, key) {
    try { window[area].removeItem(key); } catch (e) { /* noop */ }
  }
  function getGiven() {
    return storageGet('localStorage', GIVEN_KEY) || storageGet('sessionStorage', GIVEN_KEY);
  }
  function setGiven(token) {
    if (!token || getGiven() === token) return;
    if (storageSet('localStorage', GIVEN_KEY, token)) storageRemove('sessionStorage', GIVEN_KEY);
    else storageSet('sessionStorage', GIVEN_KEY, token);
  }
  function clearGiven() {
    storageRemove('localStorage', GIVEN_KEY);
    storageRemove('sessionStorage', GIVEN_KEY);
  }
  // The store refused the gift for this basket (e.g. the ebook is unavailable): stop asking this session.
  let refusedToken = '';
  function setRefused(token) {
    if (!token) return;
    refusedToken = token;
    storageSet('sessionStorage', REFUSED_KEY, token);
  }
  function isRefused(token) {
    return !!token && (refusedToken === token || storageGet('sessionStorage', REFUSED_KEY) === token);
  }

  // ---- The basket as last read ----
  let basket = null; // { token, hasTrigger, gift (its line, or null) }

  function note(cart) {
    if (!cart || !Array.isArray(cart.items)) return basket;
    const hasTrigger = cart.items.some((i) => Number(i.product_id) === TRIGGER_PRODUCT_ID);
    const gift = cart.items.find((i) => Number(i.product_id) === GIFT_PRODUCT_ID) || null;
    basket = { token: cart.token || (basket && basket.token) || '', hasTrigger, gift };
    if (!hasTrigger) clearGiven();
    else if (gift) setGiven(basket.token);
    return basket;
  }

  // The shopper took the ebook out of this basket and kept the trigger.
  function declined(b) {
    return !!(b && b.hasTrigger && !b.gift && b.token && getGiven() === b.token);
  }

  function readCart() {
    return origFetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('/cart.js ' + r.status))));
  }

  function getCartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function getSectionsList() {
    const cart = getCartElement();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      return cart.getSectionsToRender().map((s) => s.id);
    }
    return [];
  }

  function withSections(body) {
    const sections = getSectionsList();
    if (sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }
    return body;
  }

  // Redraw the drawer and the cart count without opening the drawer (the drawer script updates
  // it in place; without the drawer script, the way the free shaker does).
  function renderQuietly(sections) {
    if (!sections) return;
    if (window.HLTHCartDrawer && typeof window.HLTHCartDrawer.render === 'function') {
      window.HLTHCartDrawer.render(sections);
      return;
    }
    const parse = (html, selector) => new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    const inner = document.getElementById('CartDrawer');
    if (sections['cart-drawer'] && inner) {
      const fresh = parse(sections['cart-drawer'], '#CartDrawer');
      if (fresh) inner.innerHTML = fresh.innerHTML;
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (sections['cart-icon-bubble'] && bubble) {
      const fresh = parse(sections['cart-icon-bubble'], '.shopify-section');
      if (fresh) bubble.innerHTML = fresh.innerHTML;
    }
  }

  function publishUpdate(data) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: data });
    }
  }

  async function removeGift(line) {
    const res = await origFetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(withSections({ id: line.key, quantity: 0 })),
    });
    if (!res.ok) throw new Error('/cart/change.js ' + res.status);
    const data = await res.json();
    clearGiven();
    renderQuietly(data.sections);
    publishUpdate(data);
    return data;
  }

  // Backstop: the basket holds the trigger and not the ebook, and the shopper didn't take it out.
  async function addGiftAlone(b) {
    const gv = await ensureGiftVariant();
    if (!gv) return;
    const res = await origFetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(withSections({ items: [{ id: gv, quantity: 1 }] })),
    });
    if (!res.ok) {
      if (res.status >= 400 && res.status < 500 && res.status !== 429) setRefused(b.token);
      return;
    }
    const data = await res.json();
    setGiven(b.token);
    renderQuietly(data.sections);
    publishUpdate(data);
  }

  // ---- Basket checks: one at a time; a check asked for while one runs is done fresh after it ----
  let running = null;
  let queued = null;
  let queuedBackstop = false;

  async function check(backstop) {
    let b = note(await readCart());
    if (!b) return b;
    if (!b.hasTrigger && b.gift) {
      note(await removeGift(b.gift));
    } else if (backstop && b.hasTrigger && !b.gift && !declined(b) && !isRefused(b.token)) {
      await addGiftAlone(b);
      b = note(await readCart());
    }
    return basket;
  }

  function refresh(backstop) {
    if (!running) {
      running = check(!!backstop)
        .catch((err) => {
          console.error('[hlth-free-gift] basket check', err);
          return basket;
        })
        .finally(() => { running = null; });
      return running;
    }
    queuedBackstop = queuedBackstop || !!backstop;
    if (!queued) {
      queued = running.then(() => {
        const b = queuedBackstop;
        queued = null;
        queuedBackstop = false;
        return refresh(b);
      });
    }
    return queued;
  }

  // Resolves with the basket once no check is running or waiting.
  function settled() {
    return (queued || running || Promise.resolve()).then(() => (queued || running ? settled() : basket));
  }

  // ---- Body parsing: support FormData, URLSearchParams, JSON, plain object, string ----
  function entriesFromIterable(iter) {
    const out = {};
    for (const [k, v] of iter) out[k] = v;
    return out;
  }

  function parseFormEntries(entries) {
    const items = [];
    if ('id' in entries) {
      const item = { id: Number(entries.id), quantity: Number(entries.quantity || 1) };
      if (entries.selling_plan) item.selling_plan = entries.selling_plan;
      const props = {};
      Object.keys(entries).forEach((k) => {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m) props[m[1]] = entries[k];
      });
      if (Object.keys(props).length) item.properties = props;
      items.push(item);
    } else {
      const multiMap = {};
      Object.keys(entries).forEach((k) => {
        const m = k.match(/^items\[(\d+)\]\[(\w+)\]$/);
        if (m) {
          const [, idx, field] = m;
          (multiMap[idx] = multiMap[idx] || {})[field] = entries[k];
          return;
        }
        const m2 = k.match(/^items\[(\d+)\]\[properties\]\[(.+)\]$/);
        if (m2) {
          const [, idx, propKey] = m2;
          const slot = (multiMap[idx] = multiMap[idx] || {});
          (slot.properties = slot.properties || {})[propKey] = entries[k];
        }
      });
      Object.keys(multiMap).sort((a, b) => Number(a) - Number(b)).forEach((idx) => {
        const m = multiMap[idx];
        if (!m.id) return;
        const item = { id: Number(m.id), quantity: Number(m.quantity || 1) };
        if (m.selling_plan) item.selling_plan = m.selling_plan;
        if (m.properties) item.properties = m.properties;
        items.push(item);
      });
    }
    return {
      items,
      sections: entries.sections,
      sections_url: entries.sections_url,
    };
  }

  function normalizeJsonBody(parsed) {
    const items = [];
    if (parsed && typeof parsed === 'object') {
      if (parsed.id != null) {
        const item = { id: Number(parsed.id), quantity: Number(parsed.quantity || 1) };
        if (parsed.selling_plan) item.selling_plan = parsed.selling_plan;
        if (parsed.properties) item.properties = parsed.properties;
        items.push(item);
      } else if (Array.isArray(parsed.items)) {
        parsed.items.forEach((it) => {
          if (!it || it.id == null) return;
          const item = { id: Number(it.id), quantity: Number(it.quantity || 1) };
          if (it.selling_plan) item.selling_plan = it.selling_plan;
          if (it.properties) item.properties = it.properties;
          items.push(item);
        });
      }
    }
    return {
      items,
      sections: parsed && parsed.sections,
      sections_url: parsed && parsed.sections_url,
    };
  }

  async function parseBody(body) {
    if (!body) return null;
    if (body instanceof FormData) return parseFormEntries(entriesFromIterable(body.entries()));
    if (body instanceof URLSearchParams) return parseFormEntries(entriesFromIterable(body.entries()));
    if (typeof body === 'string') {
      try { return normalizeJsonBody(JSON.parse(body)); } catch (e) {}
      try { return parseFormEntries(entriesFromIterable(new URLSearchParams(body).entries())); } catch (e) {}
      return null;
    }
    if (typeof Blob !== 'undefined' && body instanceof Blob) {
      try { return await parseBody(await body.text()); } catch (e) { return null; }
    }
    if (typeof body === 'object') {
      // Plain object — treat as JSON-equivalent
      return normalizeJsonBody(body);
    }
    return null;
  }

  function isCartAdd(url) {
    return /\/cart\/add(\.js)?(\?|$)/.test(String(url || ''));
  }
  function isCartMutation(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(String(url || ''));
  }

  // Build the augmented init payload (always JSON for simplicity, since /cart/add accepts it).
  function buildAugmentedInit(originalInit, parsed, gv) {
    const items = parsed.items.slice();
    items.push({ id: gv, quantity: 1 });

    let sections = parsed.sections;
    let sectionsUrl = parsed.sections_url;
    if (!sections) {
      const list = getSectionsList();
      if (list.length) {
        sections = list.join(',');
        sectionsUrl = window.location.pathname;
      }
    }

    const payload = { items };
    if (sections) {
      payload.sections = sections;
      payload.sections_url = sectionsUrl;
    }

    return Object.assign({}, originalInit || {}, {
      method: 'POST',
      headers: Object.assign({}, (originalInit && originalInit.headers) || {}, {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
      }),
      body: JSON.stringify(payload),
    });
  }

  // Should this trigger add carry the ebook? Decided once any basket check that is running has
  // finished (never longer than a few seconds: a slow check must not hold up the shopper's add;
  // the backstop after the add covers it).
  async function giftFor() {
    let b = await within(settled(), 3000);
    if (b === TIMED_OUT) b = basket;
    // No note yet, or it says the shopper took the ebook out: read the basket first (one emptied
    // somewhere this script didn't see, e.g. another tab, still starts fresh).
    if (!b || declined(b)) {
      const fresh = await within(refresh(false), 3000);
      if (fresh && fresh !== TIMED_OUT) b = fresh;
    }
    if (b && (b.gift || declined(b) || isRefused(b.token))) return 0;
    return ensureGiftVariant();
  }

  // Trigger adds decide one at a time, so two quick adds can't both carry an ebook.
  let triggerTurn = Promise.resolve();

  async function addWithGift(input, init, url, parsed) {
    let release;
    const turn = new Promise((resolve) => { release = resolve; });
    const before = triggerTurn;
    triggerTurn = turn;
    let handedOver = false;
    try {
      await within(before, 4000);
      let gv = 0;
      try { gv = await giftFor(); } catch (e) { gv = 0; }
      if (!gv) return sendPlain(input, init, true);

      const res = await origFetch(url, buildAugmentedInit(init, parsed, gv));
      if (res.ok) {
        // The next trigger add waits until the basket note shows this ebook.
        handedOver = true;
        refresh(true).then(release, release);
        // A caller that asked for no drawer sections won't redraw it: tell the drawer instead.
        if (!parsed.sections) res.clone().json().then(publishUpdate).catch(noop);
        return res;
      }
      // Refused with the ebook (it may be unavailable): send the shopper's add on its own.
      const plain = await origFetch(input, init);
      if (plain.ok) {
        if (res.status >= 400 && res.status < 500 && res.status !== 429) setRefused(basket && basket.token);
        handedOver = true;
        refresh(true).then(release, release);
      }
      return plain;
    } finally {
      if (!handedOver) release();
    }
  }

  // Any other basket change: forwarded unchanged, then the basket note is brought up to date
  // (after an add, with the backstop).
  function sendPlain(input, init, isAdd) {
    const promise = origFetch(input, init);
    promise.then((res) => { if (res && res.ok) refresh(isAdd); }).catch(noop);
    return promise;
  }

  // ---- Augmenting fetch wrapper ----
  window.fetch = async function (input, init) {
    const url = typeof input === 'string' ? input : (input && (input.url || input.href)) || String(input || '');

    if (!isCartMutation(url)) return origFetch(input, init);
    if (!isCartAdd(url)) return sendPlain(input, init, false);

    let parsed = null;
    try {
      // Request objects: avoid the body-stream complexity, just pass through.
      if (!(input instanceof Request && (!init || init.body == null))) {
        await ensureTriggerVariants();
        parsed = await parseBody(init && init.body);
      }
    } catch (e) {
      console.error('[hlth-free-gift] body parse failed', e);
      parsed = null;
    }

    const items = (parsed && parsed.items) || [];
    const hasTrigger = items.some((i) => triggerVariantIds.has(Number(i.id)));
    const hasGift = items.some((i) => Number(i.id) === giftVariantId);
    if (!hasTrigger || hasGift) return sendPlain(input, init, true);
    return addWithGift(input, init, url, parsed);
  };

  // A basket change that didn't pass through fetch here (e.g. Shopify's own cart actions).
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === SOURCE) return;
      if (!running && !queued) refresh(false);
    });
  }

  // Back in this tab, or Back from checkout: the basket may have changed elsewhere.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refresh(false);
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh(false);
  });

  // ---- Init: variant IDs, then the first basket check (with the backstop) ----
  ensureTriggerVariants();
  ensureGiftVariant();
  refresh(true);
})();
